import React, { useEffect, useMemo } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { Provider, useSelector } from "react-redux";
import store from "./store";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { MantineProvider } from "@mantine/core";
import { decrypt } from "./utils";
import { GOOGLE_CLIENT_ID_ENC } from "./constants";
import { useAppTheme, applyTheme } from "./config/appTheme";
import { getPreset, DEFAULT_THEME_KEY } from "./config/themePresets";
import { hasDesignEntitlement } from "./utils/entitlements";

const Main = () => {
  const theme = useSelector((state) => state.theme);
  // Themes and branding are ORG-wide (staff accounts inherit them), so
  // entitlement is enforced here at apply time: a downgraded org reverts every
  // member to the standard Ogiso design on their next load, regardless of
  // stored preferences. Until the plan is known (pre-login, profile loading)
  // the stored theme is honored so entitled users don't get a flash.
  const reduxProfile = useSelector((state) => state?.profile?.profile);
  const designEntitled = hasDesignEntitlement(reduxProfile);

  // Active theme preset (Ogiso Light / Midnight / Ocean / Evergreen / ...).
  // Non-entitled orgs are pinned to the standard design; the user's stored
  // preference is kept so it comes back if the org upgrades again.
  const [storedThemeKey] = useAppTheme();
  const themeKey = designEntitled ? storedThemeKey : DEFAULT_THEME_KEY;
  const preset = getPreset(themeKey);

  // White-label ("Design") brand colors, applied app-wide when enabled.
  // The brand color only overrides the accent on the STANDARD preset — a
  // chosen theme (Midnight/Ocean/Evergreen) uses its own primary so the whole
  // scheme stays coherent instead of mixing a foreign brand accent into it.
  const whiteLabel = useSelector((state) => state?.profile?.profile?.business?.whiteLabel);
  const branded =
    designEntitled &&
    themeKey === DEFAULT_THEME_KEY &&
    !!(whiteLabel?.enabled && whiteLabel?.primaryColor);
  const brandPrimary = branded ? whiteLabel.primaryColor : null;
  const brandAccent = branded ? whiteLabel.accentColor || "#171717" : null;
  // Module action buttons (Add Staff/Client/Task etc.) can be colored apart
  // from the primary; the active sidebar item follows the same color. Unset
  // falls back to var(--app-primary) / the preset sidebar tokens.
  const brandModuleBtn = branded ? whiteLabel.moduleButtonColor || null : null;
  // Sidebar menu hover highlight.
  const brandHoverAccent = branded ? whiteLabel.hoverAccentColor || null : null;

  // Apply the preset's design tokens to :root, with the brand color (when on)
  // overriding the primary. Re-runs on theme or brand change.
  useEffect(() => {
    applyTheme(themeKey, brandPrimary);
  }, [themeKey, brandPrimary]);

  // Expose brand colors as standalone CSS variables too (back-compat for the
  // settings redesign's var(--brand-*) consumers).
  useEffect(() => {
    const root = document.documentElement;
    if (branded) {
      root.style.setProperty("--brand-primary", brandPrimary);
      root.style.setProperty("--brand-accent", brandAccent);
    } else {
      root.style.removeProperty("--brand-primary");
      root.style.removeProperty("--brand-accent");
    }
    // Pick a readable ink for a chosen color (YIQ contrast).
    const inkFor = (color) => {
      const hex = color.replace("#", "");
      const full = hex.length === 3 ? hex.split("").map((c) => c + c).join("") : hex;
      const [r, g, b] = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16) || 0);
      return (r * 299 + g * 587 + b * 114) / 1000 >= 140 ? "#171717" : "#FFFFFF";
    };
    if (brandModuleBtn) {
      root.style.setProperty("--app-module-btn", brandModuleBtn);
      root.style.setProperty("--app-module-btn-ink", inkFor(brandModuleBtn));
    } else {
      root.style.removeProperty("--app-module-btn");
      root.style.removeProperty("--app-module-btn-ink");
    }
    if (brandHoverAccent) {
      root.style.setProperty("--app-hover-accent", brandHoverAccent);
      root.style.setProperty("--app-hover-accent-ink", inkFor(brandHoverAccent));
    } else {
      root.style.removeProperty("--app-hover-accent");
      root.style.removeProperty("--app-hover-accent-ink");
    }
  }, [branded, brandPrimary, brandAccent, brandModuleBtn, brandHoverAccent]);

  // ── White-label browser tab title ──
  // The favicon is intentionally NOT white-labeled — it always stays the Ogiso
  // icon (declared in public/index.html) and is not user-configurable. Only the
  // tab title follows the brand name when custom design is enabled.
  const brandingActive = designEntitled && !!whiteLabel?.enabled;
  const brandName = brandingActive ? whiteLabel?.brandName || reduxProfile?.business?.companyName || null : null;
  useEffect(() => {
    document.title = brandName || "Ogiso";
  }, [brandName]);

  // Sync Mantine to the preset: colorScheme (light/dark) + accent token.
  const themed = useMemo(() => {
    const accent = brandPrimary || preset.tokens.primary;
    return {
      ...theme,
      colorScheme: preset.scheme,
      other: { ...(theme?.other || {}), yellowColor: accent },
    };
  }, [theme, preset, brandPrimary]);

  return (
    <MantineProvider theme={themed} withGlobalStyles withNormalizeCSS>
      <App />
    </MantineProvider>
  );
};

const googleClientId = decrypt(GOOGLE_CLIENT_ID_ENC);
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <Provider store={store}>
    <GoogleOAuthProvider clientId={googleClientId}>
      <Main />
    </GoogleOAuthProvider>
  </Provider>,
);

reportWebVitals();
